import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Container as MuiContainer,
  CircularProgress,
  Typography,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "../../../assets/images/logo-icon-roxa.svg";
import { QrCodeDisplay } from "./QrCodeDisplay";
import "../../../assets/scss/custom.scss";
import { useTranslation } from "react-i18next";
import { StateType } from "../../../utils/webSocketUtils";
import {
  createQRCodeWebSocket,
  WebSocketManager,
} from "../../../utils/enhancedWebSocketUtils";
import { getAuthUser } from "../../../components/GetLocalStorage";
import { logger } from "../../../utils/logger";
import { URLS } from "../../../constants";

interface WebhookQrModalProps {
  isOpen: boolean;
  toggle: () => void;
  existingSocket: WebSocket | null;
  qrCode: string | null;
  showQrCode: boolean;
  updateSessions?: () => void;
}

interface WebhookQrModalState extends StateType {}

export const WebhookQrModal: React.FC<WebhookQrModalProps> = ({
  isOpen,
  toggle,
  existingSocket,
  qrCode,
  showQrCode,
  updateSessions,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<WebhookQrModalState>({
    loading: false,
    error: null,
    qrCode: qrCode,
    showQrCode: showQrCode,
    status: null,
    name: "",
    successMessage: null,
    attemptedSubmit: false,
    buttonBlocked: false,
    webSocketLoading: true,
    nameError: null,
  });

  const [showQrWarning, setShowQrWarning] = useState(false);
  const websocketManager = useRef<WebSocketManager | null>(null);

  // Closes the modal and resets state
  const handleToggle = useCallback(() => {
    // Disconnect WebSocket when closing modal
    if (websocketManager.current) {
      websocketManager.current.disconnect();
      websocketManager.current = null;
    }

    toggle();
    setState({
      loading: false,
      error: null,
      qrCode: null,
      showQrCode: false,
      status: null,
      name: "",
      successMessage: null,
      attemptedSubmit: false,
      buttonBlocked: false,
      webSocketLoading: true,
      nameError: null,
    });
  }, [toggle]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message = event.data.startsWith("i")
          ? { qrCodeBase64: event.data, status: "SCAN_QR_CODE" }
          : JSON.parse(event.data);

        logger.debug("Received WebSocket message:", message);

        if (message.status === "SCAN_QR_CODE") {
          setState((prevState) => ({
            ...prevState,
            qrCode: "data:image/png;base64," + message.qrCodeBase64,
            showQrCode: true,
            webSocketLoading: false,
            loading: false,
            status: "SCAN_QR_CODE",
          }));
        } else if (message.status === "WORKING") {
          setState((prevState) => ({
            ...prevState,
            showQrCode: false,
            webSocketLoading: false,
            loading: false,
            status: "WORKING",
          }));

          toast.success(t("QR code successfully scanned!"));

          if (updateSessions) {
            updateSessions();
          }

          // Close modal after success with delay to allow user to see success message
          setTimeout(() => {
            handleToggle();
          }, 1000);
        }
      } catch (error) {
        logger.error("Failed to parse WebSocket message as JSON:", error);
      }
    },
    [t, updateSessions, handleToggle],
  );

  // Handle WebSocket errors
  const handleWebSocketError = useCallback(
    (error: Error) => {
      logger.error("WebSocket error observed:", error);
      setState((prevState) => ({
        ...prevState,
        webSocketLoading: false,
        loading: false,
        error: t("Failed to connect to the WebSocket. Please try again."),
      }));
    },
    [t],
  );

  // Handle WebSocket connection state changes
  const handleConnectionChange = useCallback(
    (connected: boolean) => {
      if (connected) {
        setState((prevState) => ({
          ...prevState,
          webSocketLoading: false,
          loading: true, // Keep loading until we receive data
          error: null,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          webSocketLoading: false,
          loading: false,
          error:
            prevState.error || t("Connection lost. Attempting to reconnect..."),
        }));
      }
    },
    [t],
  );

  // WebSocket connection management
  useEffect(() => {
    if (isOpen && !existingSocket) {
      setState((prevState) => ({
        ...prevState,
        loading: true,
        error: null,
      }));

      const authUser = getAuthUser();
      const companyName = authUser?.companyName || "unknown";

      try {
        websocketManager.current = createQRCodeWebSocket(
          URLS.WEBSOCKET_URL,
          companyName,
          handleWebSocketMessage,
          handleWebSocketError,
          handleConnectionChange,
        );
      } catch (error) {
        logger.error(
          "[GetQRCode] Failed to create WebSocket connection:",
          error,
        );
        handleWebSocketError(error as Error);
      }
    } else if (isOpen && existingSocket) {
      // Use existing socket - set up event handlers but don't create new connection
      existingSocket.onmessage = handleWebSocketMessage;
      existingSocket.onerror = (_event: Event) => {
        handleWebSocketError(new Error("WebSocket error"));
      };
      // Note: We intentionally do NOT set onclose handler to avoid infinite loops
    }

    // Cleanup function
    return () => {
      if (websocketManager.current) {
        websocketManager.current.disconnect();
        websocketManager.current = null;
      }
    };
  }, [
    isOpen,
    handleWebSocketMessage,
    handleWebSocketError,
    handleConnectionChange,
    existingSocket,
  ]);

  // Intercepts the dialog close event if the QR code is displayed
  const handleDialogClose = (_event: object, reason: string) => {
    if (
      (reason === "backdropClick" || reason === "escapeKeyDown") &&
      state.showQrCode
    ) {
      setShowQrWarning(true);
    } else {
      handleToggle();
    }
  };

  // Confirms closing after warning
  const handleConfirmCloseWarning = () => {
    setShowQrWarning(false);
    handleToggle();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "20px",
            padding: "20px",
          },
        }}
      >
        <DialogContent dividers>
          <MuiContainer
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div id="logo" style={{ paddingBottom: "10px", marginTop: "20px" }}>
              <img src={logo} alt={t("Logo")} width={"350"} />
            </div>
            <Typography
              variant="h6"
              component="h1"
              style={{ fontSize: "21px" }}
            >
              {t("Scan_the_QR_code_to_connect")}
            </Typography>
            {state.loading ? (
              <CircularProgress color="secondary" />
            ) : state.showQrCode && state.qrCode ? (
              <QrCodeDisplay qrCode={state.qrCode} />
            ) : state.status === "WORKING" ? (
              <Typography variant="body1">
                {t("Connected Successfully!")}
              </Typography>
            ) : (
              <Typography variant="body1">
                {t("Waiting for QR code...")}
              </Typography>
            )}
            {state.error && (
              <div
                style={{
                  position: "absolute",
                  top: "0",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                <div className="alert alert-danger" role="alert">
                  {state.error}
                </div>
              </div>
            )}
          </MuiContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={toggle}
            variant="contained"
            style={{
              backgroundColor: "#4e3699",
              borderRadius: "20px",
              color: "#fff",
            }}
          >
            {t("Close")}
          </Button>
        </DialogActions>
        <ToastContainer />
      </Dialog>

      {showQrWarning && (
        <Dialog open={showQrWarning} onClose={() => setShowQrWarning(false)}>
          <DialogTitle>{t("Atenção")}</DialogTitle>
          <DialogContent>
            <Typography>
              {t(
                "Please scan the QR Code to start a WhatsApp session. If you close it, you'll need to click the card again to recover it. Do you want to close it?",
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowQrWarning(false)}>
              {t("Cancelar")}
            </Button>
            <Button onClick={handleConfirmCloseWarning} color="error">
              {t("Fechar")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
